import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Typography, PageHeader } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { ConnectionCard } from '../../components';
import { SAMPLE_CONNECTIONS } from '../../constants';
import type { Connection, ConnectionFilter } from '../../types';
import { api } from '@/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`connections-tabpanel-${index}`}
      aria-labelledby={`connections-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ConnectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'All' | 'Payment method' | 'Processor'>('All');
  const [showFilters, setShowFilters] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [connections, setConnections] = useState<any>(SAMPLE_CONNECTIONS);
  const [loading, setLoading] = useState(false);
  const [Settlementtats, setSettlementtats] = useState('');

  const fetchPaymentGateways = async () => {
    try {
      setLoading(true);
      const url = 'merchant/v1/available-payment-gateways/';
      const response = await api.get(url);
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to fetch payment gateways:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch payment gateways on page load
  useEffect(() => {
    fetchPaymentGateways();
  }, []);

  // Breadcrumbs
  const breadcrumbConfig = getBreadcrumbConfig('/Connections');
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
    customBreadcrumbs: breadcrumbConfig.hideBreadcrumbs ? [] : undefined,
  });

  const finalBreadcrumbs = breadcrumbs.length > 0
    ? breadcrumbs
    : [
      {
        label: 'Dashboard',
        href: '/merchant/dashboard',
        onClick: () => navigate('/merchant/dashboard'),
      },
      {
        label: 'Connections',
        active: true,
      },
    ];

  // Filter connections for "Explore" tab (is_merchant_onboarded = false)
  const exploreConnections = useMemo(() => {
    return connections?.filter((conn) => {
      const matchesSearch = !searchTerm || 
        (conn.payment_gateway_name && conn.payment_gateway_name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = filterType === 'All' || conn.payment_gateway_name === filterType;
      const isNotOnboarded = conn.is_merchant_onboarded === false;
      return matchesSearch && matchesType && isNotOnboarded;
    });
  }, [connections, searchTerm, filterType]);

  // Filter connections for "Your Connections" tab (is_merchant_onboarded = true)
  const yourConnections = useMemo(() => {
    return connections?.filter((conn) => {
      const matchesSearch = !searchTerm || 
        (conn.payment_gateway_name && conn.payment_gateway_name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = filterType === 'All' || conn.payment_gateway_name === filterType;
      const isOnboarded = conn.is_merchant_onboarded === true;
      return matchesSearch && matchesType && isOnboarded;
    });
  }, [connections, searchTerm, filterType]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleConnect = (connection: Connection) => {
    // Refresh payment gateways after connection
    fetchPaymentGateways();
  };

  const handleDelete = async (connection: Connection) => {
    // Refresh payment gateways after successful deletion
    await fetchPaymentGateways();
  };

  const handleRequestIntegration = () => {
    // TODO: Open request integration modal
  };

  const activeConnections = activeTab === 0 ? exploreConnections : yourConnections;

  return (
    <Box>
      <PageHeader
        title="Connections"
        //subtitle={`Manage your payment integrations (${activeConnections.length} ${activeConnections.length === 1 ? 'connection' : 'connections'})`}
        //breadcrumbs={finalBreadcrumbs}
        showDivider={false}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="connections tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
          }}
        >
          <Tab label="Explore" />
          <Tab label="Your connections" />
        </Tabs>
      </Box>

      {/* Search, Filters, and Request Integration */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        {/* Search Bar */}
        <TextField
          variant="outlined"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            maxWidth: { xs: '100%', md: 400 },
            '& .MuiOutlinedInput-root': {
              background: 'white',
            },
          }}
        />

        {/* Filters */}
        {/* <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            size="small"
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
            }}
          >
            Add filters
          </Button>
          
          {filterType !== 'All' && (
            <Chip
              label={`Type: ${filterType}`}
              onDelete={() => setFilterType('All')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box> */}

        {/* Request Integration Button */}
        {/* <Button
          variant="contained"
          startIcon={<LinkIcon sx={{ color:'#fff'}} />}
          onClick={handleRequestIntegration}
          size="small"
          sx={{
            textTransform: 'none',
            ml: { xs: 0, md: 'auto' },
          }}
        >
          Request integration
        </Button> */}
      </Box>

      {/* Filter Panel */}
      {/* {showFilters && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as typeof filterType)}
                label="Type"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Payment method">Payment method</MenuItem>
                <MenuItem value="Processor">Processor</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      )} */}

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Explore Tab - Available Connections (not onboarded) */}
        {exploreConnections?.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1">
              No available connections found matching your search criteria.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, md: 4 }} columns={{ xs: 4, sm: 8, md: 12 }} className="connection-grid123">
            {exploreConnections?.map((connection) => (
              <Grid  key={connection.id} size={{ xs: 12, sm: 3, md: 3 }} className="connection-grid-item">
                <ConnectionCard
                  connection={connection}
                  onConnect={handleConnect}
                  onDelete={handleDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Your Connections Tab - Connected Gateways (onboarded) */}
        {yourConnections?.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1">
              You haven't connected any payment methods yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setActiveTab(0)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Explore Connections
            </Button>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} className="connection-grid123">
            {yourConnections?.map((connection) => (
              <Grid  key={connection.id} size={{ xs: 12, sm: 3, md: 3 }} className="connection-grid-item">
                <ConnectionCard
                  connection={connection}
                  onConnect={handleConnect}
                  onDelete={handleDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default ConnectionsPage;

